// Flat two-tone icons for the agent areas of the presentation page. Colours come from the theme
// (.ai-a brand, .ai-b accent, .ai-c surface), so they follow dark mode.

export type AreaIconName = 'dues' | 'requests' | 'documents' | 'maintenance' | 'governance' | 'compliance' | 'reservations';

const ICONS: Record<AreaIconName, React.ReactNode> = {
  // Card with a coin in front
  dues: (
    <>
      <rect className="ai-b" x="3" y="9" width="26" height="17" rx="3.5" />
      <rect className="ai-a" x="3" y="13" width="26" height="3.5" />
      <circle className="ai-a" cx="28" cy="27" r="8" />
      <circle className="ai-c" cx="28" cy="27" r="4.5" />
      <rect className="ai-a" x="27" y="24" width="2" height="6" rx="1" />
    </>
  ),
  // Two speech bubbles, the front one "typing"
  requests: (
    <>
      <path className="ai-b" d="M4 7a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4h-8l-6 5v-5a4 4 0 0 1-4-4z" />
      <path className="ai-a" d="M14 17a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v9a4 4 0 0 1-4 4v5l-6-5h-8a4 4 0 0 1-4-4z" />
      <circle className="ai-c" cx="20" cy="21.5" r="1.8" />
      <circle className="ai-c" cx="25" cy="21.5" r="1.8" />
      <circle className="ai-c" cx="30" cy="21.5" r="1.8" />
    </>
  ),
  // Page with folded corner and a magnifier
  documents: (
    <>
      <path className="ai-a" d="M7 6a3 3 0 0 1 3-3h12l8 8v22a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3z" />
      <path className="ai-b" d="M22 3v5a3 3 0 0 0 3 3h5z" />
      <rect className="ai-c" x="11" y="15" width="12" height="2.2" rx="1.1" />
      <rect className="ai-c" x="11" y="20" width="8" height="2.2" rx="1.1" />
      <circle className="ai-b" cx="27" cy="27" r="6.5" />
      <circle className="ai-c" cx="27" cy="27" r="3.5" />
      <rect className="ai-b" x="31" y="31.5" width="7" height="3" rx="1.5" transform="rotate(45 31 31.5)" />
    </>
  ),
  // Toolbox
  maintenance: (
    <>
      <path className="ai-b" d="M14 9a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v3h-3V9h-6v3h-3z" />
      <rect className="ai-a" x="4" y="12" width="32" height="22" rx="3.5" />
      <rect className="ai-b" x="4" y="19" width="32" height="3" />
      <rect className="ai-c" x="17" y="17" width="6" height="7" rx="1.5" />
    </>
  ),
  // Ballot box with a checked ballot going in
  governance: (
    <>
      <rect className="ai-b" x="12" y="3" width="16" height="17" rx="2" transform="rotate(-6 20 11)" />
      <path className="ai-c" d="M15.5 11.5l2.5 2.5 5-5.5" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: 'var(--surface)' }} />
      <rect className="ai-a" x="4" y="17" width="32" height="18" rx="3.5" />
      <rect className="ai-c" x="12" y="21" width="16" height="2.6" rx="1.3" />
    </>
  ),
  // Shield with a check
  compliance: (
    <>
      <path className="ai-b" d="M20 3l14 5v9c0 9-6 15.5-14 19-8-3.5-14-10-14-19V8z" />
      <path className="ai-a" d="M20 3v33c-8-3.5-14-10-14-19V8z" />
      <path d="M13.5 19.5l4.5 4.5 8.5-9" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: 'var(--surface)' }} />
    </>
  ),
  // Calendar with one booked slot
  reservations: (
    <>
      <rect className="ai-c ai-outline" x="4" y="7" width="32" height="28" rx="3.5" />
      <path className="ai-a" d="M4 10.5A3.5 3.5 0 0 1 7.5 7h25a3.5 3.5 0 0 1 3.5 3.5V15H4z" />
      <rect className="ai-b" x="11" y="3" width="3" height="8" rx="1.5" />
      <rect className="ai-b" x="26" y="3" width="3" height="8" rx="1.5" />
      <rect className="ai-dim" x="9" y="19" width="5" height="4" rx="1" />
      <rect className="ai-dim" x="17.5" y="19" width="5" height="4" rx="1" />
      <rect className="ai-b" x="26" y="19" width="5" height="4" rx="1" />
      <rect className="ai-dim" x="9" y="26" width="5" height="4" rx="1" />
      <rect className="ai-dim" x="17.5" y="26" width="5" height="4" rx="1" />
      <rect className="ai-dim" x="26" y="26" width="5" height="4" rx="1" />
    </>
  ),
};

export function AreaIcon({ name }: { name: AreaIconName }) {
  return (
    <span className="area-icon" aria-hidden="true">
      <svg viewBox="0 0 40 40" width="28" height="28">
        {ICONS[name]}
      </svg>
    </span>
  );
}
